import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Copy, KeyRound } from 'lucide-react'
import { useAuthStore } from '@/app/store/auth'
import { Button } from '@/components/ui/button'
import { formatTime } from '@/lib/dateUtils'
import { restoreRunsApi } from '../api'
import { COMMAND, newRunId } from '../useSelfTest'
import { makeRunToken, selfTestEnvelope } from '../runToken'

/**
 * Running the agent by hand on a server that has no daemon: Orchestrate makes one string, the
 * engineer pastes it into `vsrecovery run --token-stdin`, and pastes the result token the agent
 * prints at the end back here (the driver contract, sections 15 and 16).
 *
 * The run's record is made first, so the result has a row to go to. The run token is made in
 * the browser, shown once, kept in memory only and never sent to Orchestrate's back end. The
 * result token holds no secret; it is saved as the engineer's report, not as proof.
 */
const errorText = (error: unknown) => (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (error as Error).message

export function RunByHand({ onRun }: { onRun?: (runId: string) => void }) {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const engineer = useAuthStore((state) => state.user?.email ?? null)
    const [made, setMade] = useState<{ runId: string; token: string; expiresAt: string } | null>(null)
    const [pasted, setPasted] = useState('')
    const [busy, setBusy] = useState(false)
    const [problem, setProblem] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)
    const [recorded, setRecorded] = useState<string | null>(null)

    const refresh = (runId: string) => { void queryClient.invalidateQueries({ queryKey: ['restore-runs'] }); onRun?.(runId) }

    const make = async () => {
        setBusy(true); setProblem(null); setRecorded(null); setCopied(false); setPasted('')
        try {
            const runId = newRunId()
            const envelope = selfTestEnvelope(runId, engineer)
            // Orchestrate's record first. What is saved is the envelope, which for the self-test holds no secret.
            await restoreRunsApi.create({ runId, command: COMMAND, request: { type: 'runToken', ...envelope } })
            setMade({ runId, token: await makeRunToken(envelope), expiresAt: envelope.expiresAt })
            refresh(runId)
        } catch (error) { setProblem(errorText(error)) } finally { setBusy(false) }
    }
    const copy = async () => {
        if (!made) return
        try { await navigator.clipboard.writeText(made.token); setCopied(true) } catch { setProblem(t('restoreAgent.byHand.copyFailed', 'The token could not be copied. Select it and copy it by hand.')) }
    }
    const record = async () => {
        if (!made) return
        setBusy(true); setProblem(null)
        try {
            await restoreRunsApi.resultToken(made.runId, pasted)
            setRecorded(made.runId); setMade(null); setPasted('')
            refresh(made.runId)
        } catch (error) { setProblem(errorText(error)) } finally { setBusy(false) }
    }

    return (
        <section className="rounded-lg border p-4" data-testid="restore-by-hand">
            <h2 className="text-base font-semibold">{t('restoreAgent.byHand.title', 'Run it by hand on a server')}</h2>
            <p className="mt-0.5 max-w-3xl text-sm text-muted-foreground">{t('restoreAgent.byHand.help', 'For a server with no daemon. Make a run token, paste it into the agent on that server, then paste the result token the agent prints back here.')}</p>
            {!made ? (
                <Button className="mt-3" variant="outline" onClick={make} disabled={busy} data-testid="restore-make-token"><KeyRound className="mr-1.5 h-4 w-4" />{t('restoreAgent.byHand.make', 'Make a self-test run token')}</Button>
            ) : (
                <div className="mt-3 space-y-3">
                    <div>
                        <p className="text-xs font-medium">{t('restoreAgent.byHand.step1', '1. On the server, run this and paste the token when asked:')} <code className="rounded bg-muted px-1.5 py-0.5">vsrecovery run --token-stdin</code></p>
                        <div className="mt-1 flex items-start gap-2">
                            <textarea readOnly rows={3} value={made.token} aria-label={t('restoreAgent.byHand.token', 'Run token')} className="min-w-0 flex-1 rounded-md border bg-muted/40 p-2 font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
                            <Button size="sm" onClick={copy}><Copy className="mr-1.5 h-3.5 w-3.5" />{copied ? t('restoreAgent.byHand.copied', 'Copied') : t('restoreAgent.byHand.copy', 'Copy')}</Button>
                        </div>
                        <p className="mt-1 text-xs text-amber-700 dark:text-amber-400" data-testid="restore-token-warning">{t('restoreAgent.byHand.warning', 'Treat a run token as a password: do not send it by e-mail or chat. This one is for the self-test and holds no credentials. It expires at {{time}} by the server’s clock, and is not kept by Orchestrate: leave this page and it is gone.', { time: formatTime(made.expiresAt) })}</p>
                    </div>
                    <div>
                        <label className="text-xs font-medium">
                            {t('restoreAgent.byHand.step2', '2. When the run ends, paste the result token it prints (VSRR1…):')}
                            <textarea rows={3} value={pasted} onChange={(e) => setPasted(e.target.value)} className="mt-1 block w-full rounded-md border bg-background p-2 font-mono text-xs" data-testid="restore-result-token" />
                        </label>
                        <div className="mt-1 flex flex-wrap items-center gap-3">
                            <Button size="sm" onClick={record} disabled={busy || !pasted.trim()}>{t('restoreAgent.byHand.record', 'Record the result')}</Button>
                            <span className="text-xs text-muted-foreground">{t('restoreAgent.byHand.reportNote', 'Saved as your report of the run. It is not proof: the agent’s own event file is the evidence.')}</span>
                        </div>
                    </div>
                </div>
            )}
            {recorded && <p role="status" className="mt-3 text-sm" data-testid="restore-result-recorded">{t('restoreAgent.byHand.recorded', 'The result of {{runId}} has been recorded.', { runId: recorded })}</p>}
            {problem && <p role="alert" className="mt-3 text-sm font-medium text-destructive">{problem}</p>}
        </section>
    )
}
